<template>
  <Popup
    :visible="shouldShowResultDialog"
    position="center"
    :showMask="true"
    wrapperClass="rain-result-dialog"
    contentClass="rain-result-content"
    @closed="handleAfterClosed">
    <div class="rain-result">
      <RayChase class="ray-chase-bg" particleType="dot" mode="full" />

      <div class="close-btn" @click="handleClose">
        <SvgIcon name="svg/close_nobg.svg" size="18px" :fill="false" />
      </div>
      <div class="envelope"></div>
      <div class="envelope-cover">
        <div class="action-btn" @click.stop="result?.hasReward ? handleClaim() : handleClose()">
          {{ result?.hasReward ? t('立即领取') : t('知道了') }}
        </div>
      </div>
      <!-- 内容卡片 -->
      <div class="reward-clip">
        <div class="reward-wrapper">
          <div class="reward-content" v-if="result?.hasReward">
            <div class="title">
              {{ t('恭喜您！抢到红包啦！') }}
            </div>
            <div class="count-line">
              {{ $t('本轮共计抢到') }}
              <span class="num">{{ result?.hitCount || 0 }}</span>
              {{ $t('个红包') }}
            </div>
            <div class="total-cash">
              <span>{{ $t('共计') }}</span>
              <span class="cash-num">{{ formattedAmount }}</span>
            </div>
          </div>
          <div v-else class="no-reward-content">
            <div class="title">
              {{ t('很遗憾！') }}
            </div>

            <div :class="['title', { scaled: locale !== 'zh-CN' }]">
              {{ t('没有戳中有奖红包！') }}
            </div>
            <div class="tip-text">{{ $t('充值金额越高 中奖几率越大') }}</div>
            <div class="sorry-img" />
            <div v-if="nextSession" class="next-time">
              <span>
                {{
                  $t('下场活动{timeStr}准时开始', {
                    timeStr: nextSession.displayTime
                  })
                }}
              </span>
              <span>
                {{ $t('记得参与哦～') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Popup>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Popup from '@/components/base/popup/Popup.vue'
import RayChase from '@/components/RayChase.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import { useRedPacketRain } from '@/composables/useRedPacketRain'
import { formatNum } from '@/utils/numeral'
import router from '@/router'

const { shouldShowResultDialog, result, nextSession, hideResultDialog, finalizeResultDialogClose } =
  useRedPacketRain()
const { t, locale } = useI18n()

// 格式化金额
const formattedAmount = computed(() => {
  if (!result.value?.rewardAmount) return '0.00'
  return formatNum({ number: result.value.rewardAmount })
})

const handleClaim = () => {
  handleClose()
  router.push({ name: 'Welfare' })
}

const handleClose = () => {
  hideResultDialog()
}

const handleAfterClosed = () => {
  finalizeResultDialogClose()
}
</script>

<style scoped lang="scss">
:global(.rain-result-dialog) {
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

:global(.rain-result-content) {
  overflow: visible !important;
  max-width: none !important;
  max-height: none !important;
}

.rain-result {
  position: relative;
  width: 100%;
  height: 387px;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  &::after {
    content: '';
    position: absolute;
    background-image: url('@/assets/imgs/activity/rain/result_float_1.webp');
    background-size: contain;
    background-repeat: no-repeat;
    width: 285px;
    height: 296px;
    z-index: 20;
    pointer-events: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0px;
    background-image: url('@/assets/imgs/activity/rain/result_float_2.webp');
    background-size: contain;
    background-repeat: no-repeat;
    width: 322px;
    height: 220px;
    z-index: 7;
  }
}

.ray-chase-bg {
  position: absolute;
  inset: 0;
  top: 20%;
}

.close-btn {
  position: absolute;
  transform: translate(-50%, -50%);
  bottom: -70px;
  left: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 247, 194, 0.6);
  border-radius: 50%;
  background-color: rgba(255, 216, 169, 0.15);
  backdrop-filter: blur(4px);
}

// 信封
.envelope {
  margin-bottom: 15px;
  width: 225px;
  height: 218px;
  background: #c70808;
  border-radius: 16px;
  box-shadow:
    0px 0px 12px 0px #00000066,
    0px 0px 16px 0px #ff990033;
  z-index: 3;
}

.envelope-cover {
  position: absolute;
  z-index: 14;
  transform: translateX(-50%);
  left: 50%;
  bottom: 0;
  background-image: url('@/assets/imgs/activity/rain/envelope_front.webp');
  background-size: contain;
  background-repeat: no-repeat;
  width: 238px;
  height: 128px;
  display: flex;
  align-items: flex-end;
  margin-bottom: 13px;
  justify-content: center;

  .action-btn {
    width: 195px;
    height: 76px;
    color: #ac0000;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 700;
    mix-blend-mode: screen;
    background-image: url('@/assets/imgs/activity/rain/envelope_btn.webp');
    background-size: contain;
    cursor: pointer;
    margin-bottom: 20px;
    padding-top: 28px;
    text-shadow: 0.3px 0.5px 0 #ffffff;
  }
}

.reward-clip {
  text-align: center;
  position: absolute;
  bottom: 72px;
  z-index: 10;
  width: 205px;
  height: 226px;
  border-radius: 17px;
  display: flex;
  overflow: hidden;
}

.reward-wrapper {
  width: 100%;
  height: 100%;
  background-color: #ffe993;
  border-radius: 17px;
  padding: 5px;
  display: flex;
  animation: reward-slide-up 600ms cubic-bezier(0.22, 1, 0.36, 1) 200ms both;
  will-change: transform;
}

.no-reward-content,
.reward-content {
  background-color: #fff9c5;
  flex: 1;
  border: 0.5px dashed #ff7b00;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;

  .title {
    font-size: 17px;
    font-weight: 800;
    color: #ff1900;
    line-height: 22px;
    text-shadow: 0.3px 0.5px 0 #ffffff33;

    &.scaled {
      font-size: 12px;
    }
  }
}

.reward-content {
  .title {
    margin-top: 32px;
  }

  .count-line {
    color: #672403;
    margin-top: 12px;
    font-weight: 600;
    margin: 18px;

    .num {
      color: #ff1900;
    }
  }

  .total-cash {
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 180px;
    background-color: #ff0000;
    border: 1px solid #ffc800;
    box-shadow: 0px 0px 4px 0px #ffffffb2 inset;
    border-radius: 100px;
    font-size: 16px;
    font-weight: 600;
    gap: 6px;
    color: #fff;

    .cash-num {
      text-shadow: 0.5px 0.6px 0px #bc0d0d;
      font-size: 22px;
      color: #fffec5;
    }
  }
}

.no-reward-content {
  padding-top: 8px;

  .tip-text {
    color: #9c3b0a;
    font-weight: 600;
    font-size: 13px;
    line-height: 24px;
    margin-bottom: 5px;
  }

  .sorry-img {
    width: 34px;
    height: 52px;
    background-image: url('@/assets/imgs/activity/rain/no_packet.webp');
    background-size: contain;
  }

  .next-time {
    font-size: 14px;
    color: #ff5100;
    line-height: 18px;
    display: flex;
    flex-direction: column;
  }
}

.reward-info {
  margin-bottom: 32px;
}

.reward-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
}

.label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

.value {
  font-size: 18px;
  font-weight: 700;
  color: #ffd66b;
  font-family: 'DIN Alternate', sans-serif;
}

@keyframes reward-slide-up {
  0% {
    transform: translateY(155px);
  }

  100% {
    transform: translateY(0);
  }
}
</style>
